import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DataTags } from 'components/DataTags';
import { useTableTitle } from 'hooks/useTableTitle';
import { debounce, some } from 'lodash';
import {
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import {
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
} from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import type { TTypeNode } from 'utils/objectTypes';
import type { TField } from '@timeedit/types';
import { type Column, Icon } from '@timeedit/ui-components';
import {
  Form,
  Input,
  Select,
  Table,
  TreeSelect,
} from '@timeedit/ui-components/antd';
import GlobalFieldRow from './components/GlobalFieldRow';

type TColumnFieldType = {
  key: number;
  globalOrder: number;
  objectTypes: string[];
  resTemplates: string[];
} & Omit<TField, 'types'>;

const columns: Column<TColumnFieldType>[] = [
  {
    key: 'dragHandle',
    title: '',
    render: () => <Icon id="HolderOutlined" alt="Holder" />,
    width: '32px',
  },
  {
    key: 'globalOrder',
    title: 'Global Order',
    dataIndex: 'globalOrder',
    ellipsis: true,
    width: '96px',
  },
  {
    key: 'extId',
    title: 'External ID',
    dataIndex: 'extId',
    ellipsis: true,
    width: 'auto',
  },
  {
    key: 'fieldType',
    title: 'Type',
    dataIndex: 'fieldType',
    ellipsis: true,
    width: 'auto',
  },
  {
    key: 'name',
    title: 'Name',
    dataIndex: 'name',
    ellipsis: true,
    width: 'auto',
  },
  {
    key: 'objectTypes',
    title: 'Object Types',
    dataIndex: 'objectTypes',
    render: (types: string[]) => (
      <div style={{ maxWidth: '200px', minWidth: '64px' }}>
        <DataTags data={types} />
      </div>
    ),
  },
  {
    key: 'resTemplates',
    title: 'Reservation Templates',
    dataIndex: 'resTemplates',
    render: (templates: string[]) => (
      <div style={{ maxWidth: '200px', minWidth: '92px' }}>
        <DataTags data={templates} />
      </div>
    ),
    ellipsis: true,
  },
  {
    key: 'categories',
    title: 'Categories',
    dataIndex: 'categories',
    render: (categories: string[]) => (
      <div style={{ maxWidth: '200px', minWidth: '64px' }}>
        <DataTags data={categories} />
      </div>
    ),
  },
];

type GlobalFieldOrderTableProps = {
  globalFieldOrder: TField[];
  changeGlobalOrder: (moveField: TField, targetField: TField) => void;
};

const GlobalFieldOrderTable = ({
  globalFieldOrder,
  changeGlobalOrder,
}: GlobalFieldOrderTableProps) => {
  const typesMap = useAppSelector(selectObjectTypesMap);
  const reservationTemplatesMap = useAppSelector(
    selectedReservationTemplateMap
  );
  const [form] = Form.useForm();
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);
  const objectTypeTree = useAppSelector(selectObjectTypesTree);
  const [filter, setFilter] = useState<any>({
    selectedTypes: [],
    selectedResTemplates: [],
    matchText: '',
  });
  const title = useTableTitle<TColumnFieldType>({
    total: globalFieldOrder.length,
  });

  const [dataSource, setDataSource] = useState<TColumnFieldType[]>(() =>
    globalFieldOrder.map((field, index) => ({
      // Array index starts from 0, global order starts from 1
      globalOrder: index + 1,
      key: field.id,
      ...field,
      objectTypes: field.types.map((type) => typesMap[type]?.name),
      resTemplates: field.reservationTemplates.map(
        (template) => reservationTemplatesMap[template]?.name
      ),
    }))
  );

  const constructObjectTypeTreeOptions = useMemo(() => {
    const parentlessTypes = Object.values(objectTypeTree).filter(
      (treeNode) => treeNode.parents.length === 0
    );

    const construct = (typeNode: TTypeNode) => {
      const type = typesMap[typeNode.id];
      return {
        value: `${type?.id}-${type?.name}`,
        title: type?.name,
        children: typeNode.children
          ?.filter((childId) => typesMap[childId] && typesMap[childId].name)
          .map((childId) => {
            return construct(objectTypeTree[childId]);
          }),
      };
    };

    return parentlessTypes.map(construct);
  }, [objectTypeTree, typesMap]);

  const reservationTemplateOptions = useMemo(() => {
    return reservationTemplates.map((template) => ({
      value: `${template.id}-${template.name}`,
      label: template.name,
    }));
  }, [reservationTemplates]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: (event, args) => {
        const { currentCoordinates } = args;
        const delta = 39 * (event.shiftKey ? 5 : 1);
        switch (event.key) {
          case 'ArrowUp':
            return { x: currentCoordinates.x, y: currentCoordinates.y - delta };
          case 'ArrowDown':
            return { x: currentCoordinates.x, y: currentCoordinates.y + delta };
          default:
            return { x: currentCoordinates.x, y: currentCoordinates.y };
        }
      },
    })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    if (active.id !== over.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id);
        const overIndex = prev.findIndex((i) => i.key === over.id);
        const moveField = globalFieldOrder.find(
          (field) => field.id === active.id
        );
        const targetField = globalFieldOrder.find(
          (field) => field.id === over.id
        );
        changeGlobalOrder(moveField, targetField);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };

  const onValuesChange = useMemo(() => {
    return debounce((values) => {
      setFilter((prev) => ({ ...prev, ...values }));
    }, 300);
  }, []);

  const filteredRows = useMemo(() => {
    const { selectedTypes, selectedResTemplates } = filter;
    if (selectedTypes.length === 0 && selectedResTemplates.length === 0)
      return dataSource;
    return dataSource
      .filter((field) =>
        selectedTypes.length > 0
          ? some(selectedTypes, (str) => {
              const [typeId] = str.split('-');
              const type = typesMap[typeId];
              const ids = [
                ...(type?.parentFields || []),
                ...(type?.fields || []),
              ];
              return ids.includes(field.id);
            })
          : true
      )
      .filter((field) =>
        selectedResTemplates.length > 0
          ? some(selectedResTemplates, (str) => {
              const [templateId] = str.split('-');
              return some(
                field.reservationTemplates,
                (id) => String(id) === templateId
              );
            })
          : true
      );
  }, [dataSource, filter.selectedTypes, filter.selectedResTemplates, typesMap]);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={onValuesChange}
        style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
      >
        <Form.Item
          name="matchText"
          label="Search"
          style={{ minWidth: '192px' }}
        >
          <Input size="small" placeholder="Search" />
        </Form.Item>
        <Form.Item
          name="selectedTypes"
          label="Type"
          style={{ minWidth: '192px' }}
        >
          <TreeSelect
            multiple
            popupMatchSelectWidth={false}
            maxTagCount={2}
            maxTagPlaceholder={(values) => `+ ${values.length} more`}
            placeholder="Please select"
            treeDefaultExpandAll
            showSearch
            treeData={constructObjectTypeTreeOptions}
          />
        </Form.Item>
        <Form.Item
          name="selectedResTemplates"
          label="Reservation Templates"
          style={{ minWidth: '192px' }}
        >
          <Select
            mode="multiple"
            popupMatchSelectWidth={false}
            placeholder="Please select"
            options={reservationTemplateOptions}
          />
        </Form.Item>
      </Form>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={dataSource.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table<TColumnFieldType>
            sticky
            components={{
              body: {
                row: (props) => {
                  return (
                    <GlobalFieldRow
                      {...props}
                      searchString={filter.matchText}
                    />
                  );
                },
              },
            }}
            title={title}
            columns={columns}
            dataSource={filteredRows}
            pagination={false}
            rowKey="key"
            tableLayout="auto"
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default GlobalFieldOrderTable;
